import forEach from "core/src/core/util/for-each.js";
import * as gsf from "core/src/core/game-state-functions.js";


// The effects active at `renderTime`: every spawn, minus every despawn, among the
// effect events up to renderTime (in order; the walk stops at the first later one).
//
// Renderers ask this for every worm several times a frame, and the event list grows
// all round, so replaying it from the start on every call (as this used to) cost
// ~12% of the main thread in a 20-player match. The list is append-only, so each
// event is indexed once instead: for every spawned effect, the renderTime from which
// it counts and the one from which its despawn does. An event counts once
// renderTime reaches the largest time up to and including it (that is exactly when
// the in-order walk gets to it), so a query at ANY time (a replay seeks backwards)
// gives the same answer, in the same (spawn) order.
var effectIndexes = new WeakMap();
function indexEffectEvents(effectEvents) {
    var ix = effectIndexes.get(effectEvents);
    // Rebuild if the list was not just appended to (shrunk, or rewritten in place).
    if (!ix || effectEvents.length < ix.cursor || (ix.cursor > 0 && effectEvents[ix.cursor - 1] !== ix.lastEvent)) {
        ix = {cursor: 0, lastEvent: null, reach: -Infinity, all: [], byWorm: new Map(), open: new Map(), unknownFrom: Infinity};
        effectIndexes.set(effectEvents, ix);
    }
    for (; ix.cursor < effectEvents.length; ix.cursor++) {
        var ev = effectEvents[ix.cursor];
        if (ix.reach === ix.reach) { // once NaN, stays NaN
            if (typeof ev.time !== "number" || ev.time !== ev.time) {
                ix.reach = NaN; // a timeless event stops the walk for good: it and all later never count
            } else if (ev.time > ix.reach) {
                ix.reach = ev.time;
            }
        }
        var from = ix.reach;
        if (ev.type === "spawn") {
            var rec = {effect: ev.effect, from: from, until: Infinity};
            ix.all.push(rec);
            var list = ix.byWorm.get(ev.effect.wormId);
            if (!list) {
                list = [];
                ix.byWorm.set(ev.effect.wormId, list);
            }
            list.push(rec);
            var open = ix.open.get(ev.effect.id);
            if (!open) {
                open = [];
                ix.open.set(ev.effect.id, open);
            }
            open.push(rec);
        } else if (ev.type === "despawn") {
            var opened = ix.open.get(ev.effectId);
            if (opened) {
                for (var k = 0; k < opened.length; k++) {
                    opened[k].until = from;
                }
                ix.open.delete(ev.effectId);
            }
        } else if (from < ix.unknownFrom) {
            ix.unknownFrom = from;
            ix.unknownType = ev.type;
        }
        ix.lastEvent = ev;
    }
    return ix;
}

function getActiveEffects(gameState, renderTime, {wormId, effectName} = {}) {
    var ix = indexEffectEvents(gameState.effectEvents);
    if (renderTime >= ix.unknownFrom) {
        throw Error("Unknown effect event type: " + ix.unknownType);
    }
    var recs = wormId !== undefined ? (ix.byWorm.get(wormId) || []) : ix.all;
    var activeEffects = [];
    for (var i = 0; i < recs.length; i++) {
        var rec = recs[i];
        if (rec.from <= renderTime && !(rec.until <= renderTime) && (effectName === undefined || rec.effect.name === effectName)) {
            activeEffects.push(rec.effect);
        }
    }
    return activeEffects;
}

// Every effect ever spawned (whatever the time), in event order. Uses the same index.
function getEffects(gameState, {wormId, effectName} = {}) {
    var ix = indexEffectEvents(gameState.effectEvents);
    var recs = wormId ? (ix.byWorm.get(wormId) || []) : ix.all;
    var effects = [];
    for (var i = 0; i < recs.length; i++) {
        if (!effectName || recs[i].effect.name === effectName) {
            effects.push(recs[i].effect);
        }
    }
    return effects;
}

function clearPathSegmentRenderData(canvasState) {
    forEach(canvasState.pathSegmentRenderData, function (renderData) {
        renderData.segmentIndex = 0;
        renderData.latestClearSegmentIndex = -1;
    });
}

function getPathSegmentRenderData(canvasState, pathSegmentId) {
    if (!canvasState.pathSegmentRenderData[pathSegmentId]) {
        canvasState.pathSegmentRenderData[pathSegmentId] = {
            segmentIndex: 0,
            latestClearSegmentIndex: -1
        };
    }
    return canvasState.pathSegmentRenderData[pathSegmentId];
}

function getWormRenderData(canvasState, wormId) {
    if (!canvasState.wormRenderData[wormId]) {
        canvasState.wormRenderData[wormId] = {
            blinkingStartTimer: 0
        };
    }
    return canvasState.pathSegmentRenderData[wormId];
}

function createState({pathSegmentRenderData = {}} = {}) {
    return {
        renderers: [],
        prevRenderTime: 0,
        overlay: undefined,
        requestId: undefined,
        pathSegmentRenderData,
        wormRenderData: {}
    }
}


function getWormBlinkingStartTime(gameState, wormId, renderTime) {
    function getWallHackStartingTime(time, currentStartTime) {
        var activeWallHackEffects = getActiveEffects(gameState, time, {wormId, effectName: "wall_hack"});
        if (activeWallHackEffects.length === 0) {
            return null;
        }
        var startingTime = activeWallHackEffects[0].time;
        if (startingTime === currentStartTime) {
            return currentStartTime;
        }
        var earlierStartingTime = getWallHackStartingTime(startingTime, startingTime);
        return earlierStartingTime === null ? startingTime : earlierStartingTime;
    }

    return getWallHackStartingTime(renderTime, renderTime);
}

function getDrunkBubbles(gameState, time, segmentId, wormId) {
    var bubbles = [];
    var spawnFrequency = 0.2;
    var bubbleTimeSpan = 1;
    var bubbleRise = 40;
    var drunkEffects = getEffects(gameState, {wormId, effectName: "drunk"});
    // A dead worm stops emitting: no bubble SPAWNS past its death, but bubbles
    // already rising when it crashed play out their full animation (callers pass an
    // advancing clock once the worm is dead - the per-worm render clock freezes at
    // the death time, which would otherwise hang the last bubbles there forever).
    var cutoff = Infinity;
    var segs = gameState.wormPathSegments[segmentId];
    if (segs && segs.length) {
        var lastSeg = segs[segs.length - 1];
        if (lastSeg.type === "worm_died") {
            cutoff = lastSeg.startTime;
        } else {
            var deadWorm = gameState.worms && gameState.worms.find(function (w) { return w.id === wormId; });
            if (deadWorm && deadWorm.alive === false) cutoff = lastSeg.startTime + (lastSeg.duration || 0);
        }
    }
    drunkEffects.forEach(function (e) {
        for (var bubbleStartTime = e.time; bubbleStartTime < e.time + e.timeLeft; bubbleStartTime += spawnFrequency) {
            if (bubbleStartTime > cutoff) break;
            var timeDiff = time - bubbleStartTime;
            if ((timeDiff < bubbleTimeSpan && bubbleStartTime < time)) {
                var segmentData = gsf.getWormPathSegmentDataAtTime(gameState, segmentId, bubbleStartTime);
                if (segmentData) {
                    var fakeRandomRadius = ((bubbleStartTime % 0.157) * 6.37 * segmentData.segment.size) + 2;
                    var fakeRandomRise = (bubbleRise * (timeDiff / bubbleTimeSpan)) * (((bubbleStartTime % 0.141) / (0.141 * 2)) + 0.5);
                    bubbles.push({
                        x: segmentData.position.x,
                        y: segmentData.position.y - fakeRandomRise,
                        radius: fakeRandomRadius
                    })
                }
            }
        }
    });
    return bubbles;
}

function getWormHeadToRender(canvasState, gameState, pathSegmentId, renderTime) {
    var renderData = getPathSegmentRenderData(canvasState, pathSegmentId);
    var segments = gameState.wormPathSegments[pathSegmentId];
    if (!segments || segments.length === 0) {
        return null;
    }
    // Render bookkeeping can outlive the game it was built for (a preview recreates
    // its game with fresh, shorter segment lists, e.g. after a round or a player-count
    // change). Clamp a stale index so we never dereference past the end.
    if (renderData.segmentIndex >= segments.length) {
        renderData.segmentIndex = segments.length - 1;
    }
    var currentSegment = segments[renderData.segmentIndex];
    if (currentSegment.startTime <= renderTime && currentSegment.endTime >= renderTime) {
        return currentSegment
    }
    var died = segments.find(function (segment, index) {
        // Segments where a worm seemed to have died
        return segment.type === "worm_died" && renderTime > segment.startTime && index > renderData.latestClearSegmentIndex;
    });
    if (died) {
        return died;
    }
    // The render clock has overrun the latest segment we've received for this worm
    // (a late / jittery / stopped opponent, or one that crashed before we got its
    // worm_died). Hold the head at the LAST known segment's end instead of returning
    // null (which winks the head off). Keyed off the actual last segment, not the
    // render-bookkeeping index (which can lag behind, previously letting the head
    // vanish exactly when a crashed opponent stopped streaming). The head renderer
    // clamps the drawn position to the segment end, so the head just pauses at the
    // last known spot - matching local play, where a dead worm keeps its head at the
    // crash point, rather than disappearing.
    var lastSegment = segments[segments.length - 1];
    if (lastSegment) {
        if (lastSegment.type === "worm_died") {
            // Dead worm. Its worm_died marker is timestamped when the death was
            // *detected*, which for a client-authoritative worm (killed by an adopted
            // segment behind the server tick) can be AHEAD of its last real segment -
            // leaving a time gap the render clock can land in (the `died` branch above
            // only fires once renderTime passes that marker). Hold the head at the
            // death spot for any time at/after the last real segment, so it doesn't
            // wink off in that gap (e.g. an opponent on the adaptive/smooth clock).
            var prevReal = segments[segments.length - 2];
            if (!prevReal || renderTime > prevReal.endTime - 1e-9) {
                return lastSegment;
            }
        } else if (renderTime > lastSegment.endTime) {
            // Stalled/late/crashed-without-a-worm_died: hold at the last known spot.
            return lastSegment;
        }
    }
    // The cursor's segment doesn't cover renderTime and we haven't overrun the track: either the
    // cursor is momentarily stale, or renderTime sits in a TIME GAP inside the track. The one known
    // gap-maker is the switcharoonie handoff seam: the previous owner's self-predicted copy ends on
    // ITS clock, the new owner's first streamed segment starts on THEIRS, a tick or two later - a
    // 10-30ms hole. A delayed (smooth) render clock spends 1-2 frames inside it, and returning null
    // here winked the head off for exactly those frames. So: find the segment actually covering
    // renderTime (handles a stale cursor), else hold the head at the newest segment that ENDED
    // before renderTime (the pre-gap spot) - matching the hold-don't-vanish behaviour above.
    var covering = gsf.getWormPathSegmentDataAtTime(gameState, pathSegmentId, renderTime);
    if (covering && covering.segment) {
        return covering.segment;
    }
    for (var gi = segments.length - 1; gi >= 0; gi--) {
        if (segments[gi].type !== "clear" && segments[gi].endTime <= renderTime + 1e-9) {
            return segments[gi];
        }
    }
    return null;
}

export {
    clearPathSegmentRenderData,
    createState,
    getActiveEffects,
    getDrunkBubbles,
    getPathSegmentRenderData,
    getWormBlinkingStartTime,
    getWormRenderData,
    getWormHeadToRender
}
